/**
 * variableTokens — the SINGLE source of the `{{…}}` handle grammar on the frontend.
 *
 * A variable handle in the engine (see engine/workflow_rule.md §3) is one of:
 *   {{nodes.<nodeId>.<key>}}   — an upstream node's declared output
 *   {{inputs.<key>}}           — a runtime input (url, brand_name, …)
 *   {{domain.<key>}}           — read-only domain data (brand_name, …)
 *   {{gsc.<key>}}              — derived GSC data (impressions_total, …)
 *   {{item}} / {{item.<field>}}— per-element ref inside a run_per_item LLM node
 *   {{iter.item}} / {{iter.index}} — the current element + its position inside an
 *                                IterationNode body (the container loop scope)
 *
 * The UI shows these as PILLS, but the STORED value (user_prompt / system_prompt /
 * code / inputs[].description) always stays the raw `{{…}}` text — pills are a
 * visual layer only, so nothing about the engine contract changes. Every pill maps
 * 1:1 to exactly one ref token; this module is the only place that knows the syntax.
 */

/** The namespaces a ref can address. */
export type RefNamespace = "nodes" | "inputs" | "domain" | "gsc" | "item" | "iter";

/** A parsed handle reference. `raw` is the exact `{{…}}` substring (incl. braces). */
export interface VarRef {
  /** the full token incl. braces, e.g. "{{nodes.fetch.page}}" */
  raw: string;
  namespace: RefNamespace;
  /** for nodes.*: the producing node id; otherwise undefined */
  nodeId?: string;
  /** the trailing key/path after the namespace (and nodeId for nodes.*).
   *  "" for a bare {{item}}. */
  key: string;
}

/** A segment of a tokenized string: literal text, or a resolved ref. */
export type Segment =
  | { kind: "text"; text: string }
  | { kind: "ref"; ref: VarRef };

// Match a balanced {{ … }} with no nested braces. Global so we can scan a whole field.
const REF_RE = /\{\{\s*([^{}]+?)\s*\}\}/g;

/** Build the canonical raw token for a (namespace, nodeId, key) triple. */
export function buildRef(namespace: RefNamespace, key: string, nodeId?: string): string {
  if (namespace === "nodes") {
    return key ? `{{nodes.${nodeId}.${key}}}` : `{{nodes.${nodeId}}}`;
  }
  if (namespace === "item") {
    return key ? `{{item.${key}}}` : `{{item}}`;
  }
  return `{{${namespace}.${key}}}`; // inputs/domain/gsc/iter — namespace.key
}

/** Parse the inner body of a `{{…}}` (already brace-stripped, trimmed) into a VarRef,
 *  or null if it isn't a recognized namespace path. */
function parseBody(raw: string, body: string): VarRef | null {
  const dot = body.indexOf(".");
  const head = (dot === -1 ? body : body.slice(0, dot)).trim();
  const rest = dot === -1 ? "" : body.slice(dot + 1).trim();

  switch (head) {
    case "nodes": {
      // nodes.<nodeId>.<key...>  (key may itself contain dots)
      const dot2 = rest.indexOf(".");
      const nodeId = (dot2 === -1 ? rest : rest.slice(0, dot2)).trim();
      const key = dot2 === -1 ? "" : rest.slice(dot2 + 1).trim();
      if (!nodeId) return null;
      return { raw, namespace: "nodes", nodeId, key };
    }
    case "inputs":
    case "domain":
    case "gsc":
    case "iter":
      return { raw, namespace: head, key: rest };
    case "item":
      return { raw, namespace: "item", key: rest };
    default:
      return null;
  }
}

/** Extract every recognized ref in a string, in order of appearance. */
export function parseRefs(text: string): VarRef[] {
  if (!text) return [];
  const out: VarRef[] = [];
  REF_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = REF_RE.exec(text)) !== null) {
    const ref = parseBody(m[0], m[1]);
    if (ref) out.push(ref);
  }
  return out;
}

/** Split a string into ordered text/ref segments — the basis for rendering pills
 *  inline. Unrecognized `{{…}}` stays as literal text (we don't swallow it). */
export function tokenize(text: string): Segment[] {
  if (!text) return [];
  const segs: Segment[] = [];
  let last = 0;
  REF_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = REF_RE.exec(text)) !== null) {
    const ref = parseBody(m[0], m[1]);
    if (!ref) continue; // leave unrecognized braces inside the surrounding text run
    if (m.index > last) segs.push({ kind: "text", text: text.slice(last, m.index) });
    segs.push({ kind: "ref", ref });
    last = m.index + m[0].length;
  }
  if (last < text.length) segs.push({ kind: "text", text: text.slice(last) });
  return segs;
}

/**
 * Rewrite every `{{nodes.<nodeId>.<oldKey>}}` (and the bare `{{nodes.<nodeId>}}`
 * never matches here since it has no key) to use `<newKey>`. Whitespace-tolerant
 * inside the braces. Returns the rewritten string AND how many tokens changed —
 * the caller uses the count to assert the cascade rename left no stale ref.
 *
 * Only the exact (nodeId, oldKey) pair is touched; same key on a different node,
 * or a different key, is left untouched.
 */
export function rewriteNodeOutputRef(
  text: string,
  nodeId: string,
  oldKey: string,
  newKey: string
): { text: string; count: number } {
  if (!text || oldKey === newKey) return { text: text ?? "", count: 0 };
  let count = 0;
  const out = text.replace(REF_RE, (whole, body: string) => {
    const ref = parseBody(whole, body.trim());
    if (ref && ref.namespace === "nodes" && ref.nodeId === nodeId && ref.key === oldKey) {
      count += 1;
      return buildRef("nodes", newKey, nodeId);
    }
    return whole;
  });
  return { text: out, count };
}

/** True if `text` references {{nodes.<nodeId>.<key>}} at least once. */
export function referencesNodeOutput(text: string, nodeId: string, key: string): boolean {
  return parseRefs(text).some(
    (r) => r.namespace === "nodes" && r.nodeId === nodeId && r.key === key
  );
}

/** Validate a user-authored output/variable key. Engine handle keys are
 *  identifier-shaped; we enforce the same so a pill can never produce an
 *  unresolvable ref. */
export function isValidKey(key: string): boolean {
  return /^[a-z_][a-z0-9_]*$/.test(key);
}

/** Turn a raw ref into a short human label for a pill when no nicer label is
 *  available: `{{nodes.fanout_queries.queries}}` → "fanout_queries.queries"
 *  (producer.key, so the source node is visible, not just the bare key),
 *  `{{inputs.url}}` → "url", `{{item.block}}` → "block", `{{item}}` → "item".
 *  Falls back to the raw text. */
export function refLabel(raw: string): string {
  const refs = parseRefs(raw);
  const r = refs[0];
  if (!r) return raw.replace(/[{}]/g, "").trim();
  // Show producer.key so a consumed handle reads as e.g. "fanout_queries.queries"
  // (where the data comes from), not a bare "queries" that hides the source.
  if (r.namespace === "nodes") {
    return r.nodeId && r.key ? `${r.nodeId}.${r.key}` : (r.key || r.nodeId || "output");
  }
  if (r.namespace === "item") return r.key || "item";
  return r.key || r.namespace;
}

/** The ref a consumer input is bound to, parsed from its "From {{nodes.X.key}}"
 *  description — or "" if the input declares no such handle. */
export function boundRefOf(description?: string): string {
  if (!description || !description.startsWith("From {{")) return "";
  return description.replace(/^From\s+/, "").replace(/\s+—.*$/, "").trim();
}

/** §3.13(b): an input that consumes {{nodes.X.key}} INHERITS its producer's
 *  output label, so producer + consumer of one variable read identically. Given
 *  the input's description and the workflow's known handles ({ref, label}),
 *  returns the producer's label; falls back to refLabel ("node.key") when the
 *  producer isn't in scope, then to the input's own label/key. */
export function inputPillLabel(
  description: string | undefined,
  ownLabel: string | undefined,
  ownKey: string,
  handles: { ref: string; label?: string }[],
): string {
  const ref = boundRefOf(description);
  if (ref) {
    const producer = handles.find((h) => h.ref === ref);
    return producer?.label ?? refLabel(ref);
  }
  return ownLabel || ownKey;
}
